import { createContext, useCallback, useMemo } from 'react';
import * as Sentry from '@sentry/react';

// project imports
import useOnboarding from 'hooks/useOnboarding';

import { PermissionsAlertContextType } from 'model/types/permissions';

import { useAppDispatch, useAppSelector } from 'app/hooks';
import { grantTTSPermissions } from 'app/slices/merchantSlice';

export const PermissionsAlertContext = createContext<PermissionsAlertContextType | null>(null);

export function PermissionsAlertProvider({ children }: { readonly children: React.ReactNode }) {
  const { onboardingCreate } = useOnboarding();

  const { merchant, ttsPermissionsGranted, tiktokPlan, youtubePlan } = useAppSelector((store) => store.merchant);
  const dispatch = useAppDispatch();

  const handleGrantTTSPermissions = useCallback(
    (closePermissionsModal: () => void) => {
      closePermissionsModal();
      dispatch(grantTTSPermissions({ ttsPermissionsGranted: true, merchant: merchant }));
      onboardingCreate({
        currency: 'USD',
        metadata: JSON.stringify({ permissions_granted: 'true' })
      }).catch((e) => Sentry.captureException(e));
    },
    [dispatch, merchant, onboardingCreate]
  );

  const contextValue = useMemo(
    () => ({
      merchant,
      handleGrantTTSPermissions,
      tiktokPlan,
      ttsPermissionsGranted,
      youtubePlan
    }),
    [merchant, handleGrantTTSPermissions, tiktokPlan, ttsPermissionsGranted, youtubePlan]
  );

  return <PermissionsAlertContext.Provider value={contextValue}>{children}</PermissionsAlertContext.Provider>;
}
